"use client"

import React, { useState, useRef, useEffect, useCallback } from "react"
import { useRouter } from "next/navigation"

interface EffectiveRateFormProps {
  rateType: string
  state: string
  borrowerType: string
  interestOnly: boolean
  lvr: number
  rateDisplayType: string
  loanAmount: number
  loanAmountParam: string
  loanTerm: number
  loanTermParam: string
}

const LOAN_TERM_OPTIONS = [
  { label: "10 years", value: "10" },
  { label: "15 years", value: "15" },
  { label: "20 years", value: "20" },
  { label: "25 years", value: "25" },
  { label: "30 years", value: "30" },
]

export default function EffectiveRateForm({
  rateType,
  state,
  borrowerType,
  interestOnly,
  lvr,
  rateDisplayType,
  loanAmountParam,
  loanTerm,
  loanTermParam,
}: EffectiveRateFormProps) {
  const router = useRouter()

  // Use React state for form values, with localStorage fallback
  const [loanAmountValue, setLoanAmountValue] = useState(() => {
    // URL params take priority over localStorage
    if (loanAmountParam) {
      if (typeof window !== "undefined") {
        localStorage.setItem("mortgage_loanAmount", loanAmountParam)
      }
      return loanAmountParam
    }

    // Then check localStorage
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("mortgage_loanAmount")
      if (stored) return stored
    }

    // Default value if nothing else
    return "670000"
  })

  const [loanTermValue, setLoanTermValue] = useState(() => {
    // URL params take priority over localStorage
    if (loanTermParam) {
      if (typeof window !== "undefined") {
        localStorage.setItem("mortgage_loanTerm", loanTermParam)
      }
      return loanTermParam
    }

    // Then check localStorage
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("mortgage_loanTerm")
      if (stored) return stored
    }

    // Default value if nothing else
    return String(loanTerm) || "25"
  })

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const dropdownId = useRef(`dropdown-${Math.random().toString(36).substr(2, 9)}`).current

  // Save values to localStorage when they change
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("mortgage_loanAmount", loanAmountValue)
    }
  }, [loanAmountValue])

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("mortgage_loanTerm", loanTermValue)
    }
  }, [loanTermValue])

  // Check if inputs are valid - memoize with useCallback
  const isValidInput = useCallback((): boolean => {
    const loanAmount = parseFloat(loanAmountValue.replace(/[^\d.-]/g, ""))
    const loanTerm = parseFloat(loanTermValue)

    return !isNaN(loanAmount) && loanAmount > 0 && !isNaN(loanTerm) && loanTerm > 0
  }, [loanAmountValue, loanTermValue])

  // Programmatically submit the form - memoize with useCallback
  const submitForm = useCallback(() => {
    const url = `/borrowing/${rateType}?state=${state}&borrowerType=${borrowerType}&interestOnly=${interestOnly}&lvr=${lvr}&rateDisplayType=${rateDisplayType}&loanAmount=${loanAmountValue}&loanTerm=${loanTermValue}`
    router.push(url)
  }, [rateType, state, borrowerType, interestOnly, lvr, rateDisplayType, loanAmountValue, loanTermValue, router])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Auto-submit after 500ms of inactivity
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set a new timeout
    timeoutRef.current = setTimeout(() => {
      // Validate the inputs before submitting
      if (isValidInput()) {
        submitForm()
      }
    }, 500)

    // Cleanup timeout when component unmounts or inputs change again
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [loanAmountValue, loanTermValue, isValidInput, submitForm])

  const handleLoanAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoanAmountValue(e.target.value)
  }

  const handleLoanTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoanTermValue(e.target.value)
  }

  const handleLoanTermFocus = () => {
    setIsDropdownOpen(true)
  }

  const setLoanTermOption = (value: string) => {
    setLoanTermValue(value)
    setIsDropdownOpen(false)
    // Remove auto-refocus to prevent dropdown from reopening
  }

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center gap-4">
        {/* Loan Amount Input */}
        <div className="flex items-center">
          <span className="mr-2 text-sm font-medium text-gray-700">Loan Amount:</span>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
            <input
              type="text"
              name="loanAmount"
              value={loanAmountValue}
              onChange={handleLoanAmountChange}
              className="w-32 rounded border border-gray-300 py-1.5 pl-7 pr-3 text-sm"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="670,000"
            />
          </div>
        </div>

        {/* Vertical separator */}
        <div className="h-6 w-px bg-gray-300"></div>

        {/* Loan Term Input with Dropdown */}
        <div className="flex items-center">
          <span className="mr-2 text-sm font-medium text-gray-700">Loan Term:</span>
          <div
            className="relative"
            onClick={() => setIsDropdownOpen(true)} // Add click handler to the entire container
          >
            <input
              ref={inputRef}
              type="text"
              name="loanTerm"
              value={loanTermValue}
              onChange={handleLoanTermChange}
              onFocus={handleLoanTermFocus}
              onClick={handleLoanTermFocus}
              className="w-20 rounded border border-gray-300 px-3 py-1.5 text-sm"
              step="0.1"
              inputMode="decimal"
              role="combobox"
              aria-controls={dropdownId}
              aria-expanded={isDropdownOpen}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">years</span>

            {/* Dropdown menu */}
            {isDropdownOpen && (
              <div
                id={dropdownId}
                ref={dropdownRef}
                className="absolute left-0 top-full z-10 mt-1 w-32 rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                role="listbox"
              >
                {LOAN_TERM_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation() // Stop event from bubbling up to parent container
                      setLoanTermOption(option.value)
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50"
                    role="option"
                    aria-selected={loanTermValue === option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
